import logger from '../utils/logger';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Activity, ArrowRight, Clock, User as UserIcon, Filter, AlertTriangle, ShieldCheck, X } from 'lucide-react';

const RESOURCE_LABELS = {
    lead: 'Leads',
    employee: 'Employees',
    department: 'Departments',
    designation: 'Designations',
    payroll: 'Payroll',
    leave: 'Leaves',
    loan: 'Loans',
    advance: 'Advances',
    visit: 'Visits',
    expense: 'Expenses',
    project: 'Projects',
    task: 'Tasks',
    asset: 'Assets',
    coaching: 'Coaching',
    catchup: 'Catch-up',
    crm_config: 'CRM Config',
    share_settings: 'Auto-Share',
    incentive_slab: 'Incentive Slabs',
    crm_product: 'CRM Products',
    lead_category: 'Lead Categories',
    other: 'Other',
};

const AuditLog = () => {
    const { canManage } = useAuth();
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ resource_type: '', method: '', actor_id: '', days: 7, limit: 100 });
    const [anomalies, setAnomalies] = useState(null);
    const [scanning, setScanning] = useState(false);

    const fetchAnomalies = useCallback(async () => {
        if (!canManage) return;
        try {
            const { data } = await axios.get(`${API_URL}/api/audit/anomalies?status=open&days=30&limit=20`, { withCredentials: true });
            setAnomalies(data);
        } catch (e) { logger.error('anomalies fetch', e); }
    }, [canManage]);

    const triggerScan = async () => {
        setScanning(true);
        try {
            await axios.post(`${API_URL}/api/audit/anomalies/scan?window_hours=72`, {}, { withCredentials: true });
            await fetchAnomalies();
        } catch (e) {
            alert(e.response?.data?.detail || 'Scan failed');
        } finally {
            setScanning(false);
        }
    };

    const dismissAnomaly = async (id) => {
        const note = window.prompt('Optional note about why this is expected:');
        if (note === null) return;
        try {
            await axios.patch(`${API_URL}/api/audit/anomalies/${id}/dismiss?note=${encodeURIComponent(note)}`, {}, { withCredentials: true });
            await fetchAnomalies();
        } catch (e) {
            alert(e.response?.data?.detail || 'Dismiss failed');
        }
    };

    const fetchAuditData = useCallback(async () => {
        if (!canManage) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.resource_type) params.append('resource_type', filters.resource_type);
            if (filters.method) params.append('method', filters.method);
            if (filters.actor_id) params.append('actor_id', filters.actor_id);
            params.append('days', String(filters.days));
            params.append('limit', String(filters.limit));
            const [logsRes, summaryRes] = await Promise.all([
                axios.get(`${API_URL}/api/audit/logs?${params.toString()}`, { withCredentials: true }),
                axios.get(`${API_URL}/api/audit/logs/summary?days=${filters.days}`, { withCredentials: true }),
            ]);
            setRows(logsRes.data.rows || []);
            setSummary(summaryRes.data);
        } catch (e) {
            logger.error('audit fetch', e);
        } finally {
            setLoading(false);
        }
    }, [canManage, filters]);

    useEffect(() => { fetchAuditData(); fetchAnomalies(); }, [fetchAuditData, fetchAnomalies]);

    const fmt = (iso) => {
        if (!iso) return '';
        return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    if (!canManage) {
        return <div className="p-6 text-center text-slate-500" data-testid="audit-no-access">Admins only.</div>;
    }

    return (
        <div className="space-y-6" data-testid="audit-log-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        Audit Log
                    </h1>
                    <p className="text-slate-500 mt-1">SOC-2-friendly traceability for every CRM mutation. Who changed what, when.</p>
                </div>
                <Button variant="outline" className="rounded-lg" onClick={fetchAuditData} disabled={loading} data-testid="audit-refresh-btn">
                    {loading ? 'Loading...' : 'Refresh'}
                </Button>
            </div>

            {/* Summary KPI cards */}
            {summary && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card className="bg-white border border-slate-200 rounded-xl shadow-sm border-t-4 border-t-[#2563eb] overflow-hidden">
                        <CardContent className="p-5">
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Total Events</p>
                            <p className="text-2xl font-extrabold text-slate-900 mt-2 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>{summary.total}</p>
                            <p className="text-xs text-slate-500 mt-1">Last {summary.window_days} days</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-white border border-slate-200 rounded-xl shadow-sm border-t-4 border-t-emerald-500 overflow-hidden">
                        <CardContent className="p-5">
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Top Actor</p>
                            <p className="text-base font-bold text-slate-900 mt-2 truncate" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                {summary.top_actors?.[0]?.actor_name || '—'}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">{summary.top_actors?.[0]?.count || 0} actions</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-white border border-slate-200 rounded-xl shadow-sm border-t-4 border-t-amber-500 overflow-hidden">
                        <CardContent className="p-5">
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Top Resource</p>
                            <p className="text-base font-bold text-slate-900 mt-2 truncate" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                {RESOURCE_LABELS[summary.by_resource?.[0]?.resource_type] || summary.by_resource?.[0]?.resource_type || '—'}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">{summary.by_resource?.[0]?.count || 0} actions</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-white border border-slate-200 rounded-xl shadow-sm border-t-4 border-t-purple-500 overflow-hidden">
                        <CardContent className="p-5">
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Most Used Method</p>
                            <p className="text-base font-bold text-slate-900 mt-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                {summary.by_method?.[0]?.method || '—'}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">{summary.by_method?.[0]?.count || 0} calls</p>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* 🚨 Anomaly Detective */}
            {anomalies && (
                <Card className={`rounded-xl shadow-sm border-2 overflow-hidden ${anomalies.counts.high_open > 0 ? 'border-red-300 bg-red-50/40' : anomalies.counts.open > 0 ? 'border-amber-300 bg-amber-50/30' : 'border-emerald-200 bg-emerald-50/30'}`} data-testid="anomaly-card">
                    <CardHeader className={`border-b py-3 ${anomalies.counts.high_open > 0 ? 'border-red-200' : anomalies.counts.open > 0 ? 'border-amber-200' : 'border-emerald-200'}`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                {anomalies.counts.open === 0 ? (
                                    <ShieldCheck className="h-5 w-5 text-emerald-600" />
                                ) : (
                                    <AlertTriangle className={`h-5 w-5 ${anomalies.counts.high_open > 0 ? 'text-red-600' : 'text-amber-600'}`} />
                                )}
                                <CardTitle className="text-base font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    {anomalies.counts.open === 0 ? 'All clear — no anomalies' : `${anomalies.counts.open} anomal${anomalies.counts.open === 1 ? 'y' : 'ies'} need${anomalies.counts.open === 1 ? 's' : ''} review`}
                                </CardTitle>
                                {anomalies.counts.high_open > 0 && (
                                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-red-600 text-white">
                                        {anomalies.counts.high_open} HIGH
                                    </span>
                                )}
                            </div>
                            <Button variant="outline" size="sm" className="rounded-lg" onClick={triggerScan} disabled={scanning} data-testid="anomaly-scan-btn">
                                {scanning ? 'Scanning...' : 'Re-scan now'}
                            </Button>
                        </div>
                    </CardHeader>
                    {anomalies.rows && anomalies.rows.length > 0 && (
                        <CardContent className="p-3">
                            <ul className="space-y-2">
                                {anomalies.rows.slice(0, 8).map((a) => (
                                    <li key={a._id} className="flex items-start gap-3 bg-white border border-slate-200 rounded-lg p-3 shadow-sm" data-testid={`anomaly-row-${a._id}`}>
                                        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded mt-0.5 shrink-0 ${a.severity === 'high' ? 'bg-red-100 text-red-700' : a.severity === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{a.severity}</span>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-slate-900">{a.description}</p>
                                            <p className="text-[11px] text-slate-500 mt-0.5">
                                                <span className="font-semibold">{a.rule.replace(/_/g, ' ')}</span>
                                                {a.actor_name && <span> · by {a.actor_name}</span>}
                                                {a.occurrences > 1 && <span> · seen {a.occurrences}× today</span>}
                                                <span> · {fmt(a.created_at)}</span>
                                            </p>
                                        </div>
                                        <Button variant="ghost" size="sm" className="rounded-lg shrink-0 text-slate-500 hover:text-slate-900" onClick={() => dismissAnomaly(a._id)} data-testid={`anomaly-dismiss-${a._id}`} title="Mark as expected">
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                        </CardContent>
                    )}
                </Card>
            )}

            {/* Filters */}
            <Card className="rounded-xl shadow-sm border-slate-200 overflow-hidden">
                <CardContent className="p-4">
                    <div className="flex items-center flex-wrap gap-2">
                        <Filter className="h-4 w-4 text-slate-400" />
                        <select
                            value={filters.days}
                            onChange={(e) => setFilters({ ...filters, days: parseInt(e.target.value) })}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs bg-slate-50"
                            data-testid="audit-filter-window"
                        >
                            <option value={1}>Last 24 hours</option>
                            <option value={7}>Last 7 days</option>
                            <option value={30}>Last 30 days</option>
                            <option value={90}>Last 90 days</option>
                        </select>
                        <select
                            value={filters.resource_type}
                            onChange={(e) => setFilters({ ...filters, resource_type: e.target.value })}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs bg-slate-50"
                            data-testid="audit-filter-resource"
                        >
                            <option value="">All resources</option>
                            {Object.entries(RESOURCE_LABELS).map(([k, v]) => (
                                <option key={k} value={k}>{v}</option>
                            ))}
                        </select>
                        <select
                            value={filters.method}
                            onChange={(e) => setFilters({ ...filters, method: e.target.value })}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs bg-slate-50"
                            data-testid="audit-filter-method"
                        >
                            <option value="">All methods</option>
                            <option value="POST">POST (create)</option>
                            <option value="PUT">PUT (update)</option>
                            <option value="PATCH">PATCH (partial)</option>
                            <option value="DELETE">DELETE</option>
                        </select>
                        <span className="text-xs text-slate-500 ml-auto">Showing <strong>{rows.length}</strong> of {summary?.total ?? '?'} events</span>
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card className="rounded-xl shadow-sm border-slate-200 overflow-hidden">
                <CardHeader className="border-b border-slate-200 bg-slate-50/50 py-3">
                    <CardTitle className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        <Activity className="h-4 w-4 text-[#2563eb]" /> Recent Events
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full" data-testid="audit-table">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50">
                                    <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">When</th>
                                    <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Actor</th>
                                    <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Method</th>
                                    <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Resource</th>
                                    <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Path / Description</th>
                                    <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Diff</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 && (
                                    <tr><td colSpan={6} className="p-8 text-center text-slate-400 text-sm">No events in this window.</td></tr>
                                )}
                                {rows.map((row) => {
                                    const isError = row.status_code && row.status_code >= 400;
                                    return (
                                    <tr key={row._id} className={`border-b border-slate-100 hover:bg-slate-50 ${isError ? 'bg-red-50/50' : ''}`} data-testid={`audit-row-${row._id}`}>
                                        <td className="p-3 text-xs text-slate-600 tabular-nums whitespace-nowrap">
                                            <Clock className="h-3 w-3 inline mr-1 text-slate-400" />{fmt(row.timestamp)}
                                        </td>
                                        <td className="p-3 text-xs">
                                            <p className="font-medium text-slate-900 flex items-center gap-1"><UserIcon className="h-3 w-3 text-slate-400" />{row.actor_name || '—'}</p>
                                            <p className="text-[10px] text-slate-400">{row.actor_role || ''}</p>
                                        </td>
                                        <td className="p-3 text-xs">
                                            {row.method && (
                                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                                    row.method === 'POST' ? 'bg-blue-100 text-blue-700' :
                                                    row.method === 'PUT' ? 'bg-amber-100 text-amber-700' :
                                                    row.method === 'PATCH' ? 'bg-purple-100 text-purple-700' :
                                                    row.method === 'DELETE' ? 'bg-red-100 text-red-700' :
                                                    'bg-slate-100 text-slate-600'
                                                }`}>{row.method}</span>
                                            )}
                                            {row.is_business_event && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 ml-1">EVENT</span>}
                                            {row.status_code && (
                                                <span className={`text-[10px] ml-1 font-medium ${
                                                    isError ? 'text-red-600' : 'text-slate-400'
                                                }`} data-testid={isError ? 'audit-error-status' : undefined}>
                                                    {row.status_code}
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-3 text-xs text-slate-600">
                                            <span className="font-medium">{RESOURCE_LABELS[row.resource_type] || row.resource_type}</span>
                                            {row.resource_id && <span className="text-slate-400 block text-[10px] tabular-nums">#{row.resource_id.slice(-8)}</span>}
                                        </td>
                                        <td className="p-3 text-xs text-slate-600">
                                            <p className="truncate max-w-md font-mono text-[11px]">{row.description || row.path}</p>
                                        </td>
                                        <td className="p-3 text-xs">
                                            {row.is_business_event && row.before && row.after ? (
                                                <div className="text-[10px] font-mono space-y-0.5">
                                                    {Object.entries(row.after).slice(0, 2).map(([k, v]) => {
                                                        const oldV = row.before[k];
                                                        if (oldV === v || v === undefined || v === null) return null;
                                                        return (
                                                            <div key={k}>
                                                                <span className="text-slate-500">{k}:</span> <span className="text-red-500 line-through">{String(oldV ?? '∅')}</span>
                                                                <ArrowRight className="inline h-2.5 w-2.5 mx-1 text-slate-400" />
                                                                <span className="text-emerald-600 font-medium">{String(v)}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <span className="text-slate-300">—</span>
                                            )}
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default AuditLog;
